import { useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextFieldMUI from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import {
  FIELD_VIDEO_YOUTUBE_URL_INVALID,
  FIELD_VIDEOS_REPEATED,
  YOUTUBE_VIDEO_LINK,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import TooltipOptimization from "../../../Tooltip/TooltipOptimization";
import { REGEX_YOUTUBE_URL } from "../../../../utils/regex";
import { textFieldInputSx } from "../../../../styles";
import {
  findInvalidYoutubeUrlEntry,
  getYoutubeVideoAddValidationResult,
} from "../../../../utils/youtubeVideoValidation";
import { startValidateYoutubeVideos } from "../../../../actions/user";

const YoutubeUrlInputField = ({
  currentUrls = [],
  maxVideos = 15,
  disabled = false,
  showTooltip = false,
  titleTooltip = "",
  actionTooltip = "",
  onCallbackAddUrl = () => {},
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslationApp();
  const [draftUrl, setDraftUrl] = useState("");
  const [draftError, setDraftError] = useState("");
  const [isAddingVideo, setIsAddingVideo] = useState(false);

  const videos = (currentUrls || []).filter(Boolean);
  const canAddVideo = videos.length < maxVideos;

  const onAddVideo = async () => {
    const trimmedUrl = draftUrl.trim();

    if (!trimmedUrl) {
      setDraftError("");
      return;
    }

    if (!REGEX_YOUTUBE_URL.test(trimmedUrl)) {
      setDraftError(t(FIELD_VIDEO_YOUTUBE_URL_INVALID));
      return;
    }

    if (videos.length >= maxVideos) {
      return;
    }

    const isDuplicate = videos.some(
      (videoUrl) => videoUrl.trim().toLowerCase() === trimmedUrl.toLowerCase()
    );

    if (isDuplicate) {
      setDraftError(t(FIELD_VIDEOS_REPEATED));
      return;
    }

    setIsAddingVideo(true);

    try {
      const response = await dispatch(startValidateYoutubeVideos([trimmedUrl]));

      if (response?.ok === false && response.invalidUrls) {
        const invalidEntry = findInvalidYoutubeUrlEntry({
          invalidUrls: response.invalidUrls,
          url: trimmedUrl,
        });

        if (invalidEntry) {
          const validationResult = getYoutubeVideoAddValidationResult({
            isPublic: invalidEntry.isPublic,
            isDurationValid: invalidEntry.isDurationValid,
            t,
          });

          if (validationResult.errorMessage) {
            setDraftError(validationResult.errorMessage);
            return;
          }
        }
      }

      onCallbackAddUrl({ youtubeUrl: trimmedUrl });
      setDraftUrl("");
      setDraftError("");
    } catch {
      onCallbackAddUrl({ youtubeUrl: trimmedUrl });
      setDraftUrl("");
      setDraftError("");
    } finally {
      setIsAddingVideo(false);
    }
  };

  const onDraftUrlChange = (event) => {
    setDraftUrl(event.target.value);
    if (draftError) {
      setDraftError("");
    }
  };

  const onDraftUrlKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onAddVideo();
    }
  };

  if (disabled) {
    return null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
      >
        <TextFieldMUI
          label={t(YOUTUBE_VIDEO_LINK)}
          variant="filled"
          fullWidth
          value={draftUrl}
          onChange={onDraftUrlChange}
          onKeyDown={onDraftUrlKeyDown}
          error={Boolean(draftError)}
          disabled={!canAddVideo || isAddingVideo}
          sx={{ flex: 1, ...textFieldInputSx }}
        />
        <TooltipOptimization
          isOpen={showTooltip}
          titleTooltip={titleTooltip}
          actionTooltip={actionTooltip}
        >
          <IconButton
            onClick={onAddVideo}
            size="small"
            disabled={!canAddVideo || isAddingVideo}
          >
            {isAddingVideo ? <CircularProgress size={20} /> : <AddIcon />}
          </IconButton>
        </TooltipOptimization>
      </Box>
      {draftError ? (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, mx: 0 }}>
          {draftError}
        </Typography>
      ) : null}
    </Box>
  );
};

export default YoutubeUrlInputField;
